/**
 * usePWAInstall Hook
 *
 * Manages the PWA installation prompt for Android devices.
 * Captures the `beforeinstallprompt` event and provides a way to trigger
 * the install prompt programmatically (e.g., from a custom "Install App" button).
 *
 * Note: This is Android/Chrome-specific. iOS requires manual "Add to Home Screen"
 * via Safari's share menu and doesn't fire this event.
 *
 * IMPORTANT: This hook registers the service worker on mount to ensure Chrome
 * can evaluate PWA installability criteria. The beforeinstallprompt event only
 * fires when the SW is registered AND has a fetch handler.
 */

import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface PWADebugInfo {
  serviceWorkerStatus: 'checking' | 'registered' | 'not-registered' | 'not-supported';
  serviceWorkerScope: string | null;
  serviceWorkerState: string | null;
  manifestStatus: 'checking' | 'loaded' | 'error' | 'not-found';
  manifestError: string | null;
  beforeInstallPromptFired: boolean;
  isSecureContext: boolean;
  iconsAccessible: boolean | null;
  userAgent: string;
}

interface UsePWAInstallReturn {
  /** Whether the app can be installed (prompt is available) */
  canInstall: boolean;
  /** Whether the app is already installed (running in standalone mode) */
  isInstalled: boolean;
  /** Whether we're currently showing the install prompt */
  isPrompting: boolean;
  /** Trigger the install prompt. Returns true if accepted, false if dismissed */
  promptInstall: () => Promise<boolean>;
  /** Platform info for conditional UI */
  platform: {
    isAndroid: boolean;
    isIOS: boolean;
    isChrome: boolean;
  };
  /** Debug info for troubleshooting PWA install issues */
  debugInfo: PWADebugInfo;
}

/**
 * Register the service worker for PWA installability.
 * Chrome requires a registered SW with a fetch handler to show install prompts.
 * IMPORTANT: SW must be served from root (/) for scope to cover manifest's start_url.
 */
async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    // Check if already registered at root scope
    const existingReg = await navigator.serviceWorker.getRegistration('/');
    if (existingReg) {
      return; // Already registered
    }

    // Register the service worker from root for correct scope
    await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  } catch (error) {
    // Silent fail - SW registration is best-effort for PWA install
    console.warn('[PWA] Service worker registration failed:', error);
  }
}

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);
  const [debugInfo, setDebugInfo] = useState<PWADebugInfo>({
    serviceWorkerStatus: 'checking',
    serviceWorkerScope: null,
    serviceWorkerState: null,
    manifestStatus: 'checking',
    manifestError: null,
    beforeInstallPromptFired: false,
    isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : false,
    iconsAccessible: null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  });

  // Detect platform
  const platform = (() => {
    if (typeof navigator === 'undefined') {
      return { isAndroid: false, isIOS: false, isChrome: false };
    }
    const ua = navigator.userAgent;
    return {
      isAndroid: /Android/.test(ua),
      isIOS: /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
      isChrome: /Chrome\//.test(ua) && !/Edg\//.test(ua),
    };
  })();

  useEffect(() => {
    // Check service worker status
    const checkServiceWorker = async () => {
      if (!('serviceWorker' in navigator)) {
        setDebugInfo(prev => ({ ...prev, serviceWorkerStatus: 'not-supported' }));
        return;
      }

      try {
        // Check for registration at root scope (required for PWA install)
        const registration = await navigator.serviceWorker.getRegistration('/');
        if (registration) {
          // Check if scope covers root
          const scopeCoversRoot = registration.scope.endsWith('/') || registration.scope === location.origin;
          // Get active worker state
          const activeWorker = registration.active || registration.installing || registration.waiting;
          const workerState = activeWorker?.state || 'no worker';

          setDebugInfo(prev => ({
            ...prev,
            serviceWorkerStatus: 'registered',
            serviceWorkerScope: registration.scope + (scopeCoversRoot ? '' : ' (wrong scope!)'),
            serviceWorkerState: workerState,
          }));

          // Listen for state changes
          if (activeWorker) {
            activeWorker.addEventListener('statechange', () => {
              setDebugInfo(prev => ({
                ...prev,
                serviceWorkerState: activeWorker.state,
              }));
            });
          }
        } else {
          // Check if there's a registration at a different scope
          const anyReg = await navigator.serviceWorker.getRegistration();
          if (anyReg) {
            setDebugInfo(prev => ({
              ...prev,
              serviceWorkerStatus: 'registered',
              serviceWorkerScope: anyReg.scope + ' (wrong scope - needs /)',
              serviceWorkerState: anyReg.active?.state || 'unknown',
            }));
          } else {
            setDebugInfo(prev => ({ ...prev, serviceWorkerStatus: 'not-registered', serviceWorkerState: null }));
          }
        }
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          serviceWorkerStatus: 'not-registered',
          serviceWorkerScope: String(error),
          serviceWorkerState: null,
        }));
      }
    };

    // Check manifest status and icon accessibility
    const checkManifest = async () => {
      try {
        const response = await fetch('/static/manifest.json');
        if (response.ok) {
          const manifest = await response.json();
          // Basic validation
          const issues: string[] = [];
          if (!manifest.name) issues.push('missing name');
          if (!manifest.short_name) issues.push('missing short_name');
          if (!manifest.start_url) issues.push('missing start_url');
          if (!manifest.display) issues.push('missing display');
          if (!manifest.icons || manifest.icons.length === 0) issues.push('missing icons');
          else {
            const has192 = manifest.icons.some((i: { sizes?: string }) => i.sizes?.includes('192'));
            const has512 = manifest.icons.some((i: { sizes?: string }) => i.sizes?.includes('512'));
            if (!has192) issues.push('missing 192x192 icon');
            if (!has512) issues.push('missing 512x512 icon');
          }

          if (issues.length > 0) {
            setDebugInfo(prev => ({
              ...prev,
              manifestStatus: 'error',
              manifestError: issues.join(', '),
            }));
          } else {
            setDebugInfo(prev => ({ ...prev, manifestStatus: 'loaded', manifestError: null }));
          }

          // Check if icons are actually accessible
          if (manifest.icons && manifest.icons.length > 0) {
            try {
              const icon192 = manifest.icons.find((i: { sizes?: string }) => i.sizes?.includes('192'));
              const icon512 = manifest.icons.find((i: { sizes?: string }) => i.sizes?.includes('512'));

              const iconChecks = await Promise.all([
                icon192 ? fetch(icon192.src, { method: 'HEAD' }).then(r => r.ok).catch(() => false) : Promise.resolve(false),
                icon512 ? fetch(icon512.src, { method: 'HEAD' }).then(r => r.ok).catch(() => false) : Promise.resolve(false),
              ]);

              const allIconsOk = iconChecks.every(Boolean);
              setDebugInfo(prev => ({ ...prev, iconsAccessible: allIconsOk }));
            } catch {
              setDebugInfo(prev => ({ ...prev, iconsAccessible: false }));
            }
          }
        } else {
          setDebugInfo(prev => ({
            ...prev,
            manifestStatus: 'error',
            manifestError: `HTTP ${response.status}`,
          }));
        }
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          manifestStatus: 'error',
          manifestError: String(error),
        }));
      }
    };

    // Run checks
    checkServiceWorker();
    checkManifest();

    // Register service worker early for PWA installability
    // This is required for Chrome to fire beforeinstallprompt
    registerServiceWorker();

    // Check if already installed (standalone mode)
    const checkInstalled = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true);
      setIsInstalled(isStandalone);
    };

    checkInstalled();

    // Listen for display mode changes (in case user installs while page is open)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsInstalled(e.matches);
    };
    mediaQuery.addEventListener('change', handleChange);

    // Capture the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default mini-infobar from appearing
      e.preventDefault();
      // Save the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Update debug info
      setDebugInfo(prev => ({ ...prev, beforeInstallPromptFired: true }));
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    setIsPrompting(true);

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      // Clear the deferred prompt - it can only be used once
      setDeferredPrompt(null);

      return outcome === 'accepted';
    } catch (error) {
      console.error('[usePWAInstall] Error showing install prompt:', error);
      return false;
    } finally {
      setIsPrompting(false);
    }
  }, [deferredPrompt]);

  return {
    canInstall: deferredPrompt !== null,
    isInstalled,
    isPrompting,
    promptInstall,
    platform,
    debugInfo,
  };
}
